import React, { useState, useRef, useEffect } from 'react';
import { RotateCcw, CloudUpload, Play } from 'lucide-react';

function RecordingReview({ videoBlob, question, mode, onRetake, onSave, uploading }) {
  const videoRef = useRef(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [caption, setCaption] = useState('');

  useEffect(() => {
    if (videoBlob) {
      const url = URL.createObjectURL(videoBlob);
      setVideoUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [videoBlob]);

  const togglePlay = () => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(() => {});
    }
    setIsPlaying(!isPlaying);
  };

  const handleSave = () => {
    onSave(mode === 'moment' ? caption : undefined);
  };

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Question text at top */}
      <div className="p-4 pt-12 text-center">
        {question && mode !== 'moment' && (
          <p className="text-white font-semibold">{question.question}</p>
        )}
        {mode === 'moment' && (
          <p className="text-white font-semibold">Review your moment</p>
        )}
      </div>

      {/* Video playback in portrait frame */}
      <div className="flex-1 flex items-center justify-center bg-black overflow-hidden pb-48">
        {videoUrl && (
          <div className="relative h-full max-w-full aspect-[9/16]">
            <video
              ref={videoRef}
              src={videoUrl}
              className="h-full w-full object-contain"
              playsInline
              onClick={togglePlay}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
            />
            {/* Play overlay */}
            {!isPlaying && (
              <div
                className="absolute inset-0 flex items-center justify-center cursor-pointer"
                onClick={togglePlay}
              >
                <div className="w-16 h-16 rounded-full bg-black/40 flex items-center justify-center">
                  <Play className="w-8 h-8 text-white ml-1" />
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Upload overlay */}
      {uploading && (
        <div className="absolute inset-0 z-20 bg-black/70 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400 mx-auto mb-3" />
            <p className="text-white font-medium">Uploading memory...</p>
          </div>
        </div>
      )}

      {/* Bottom controls */}
      <div className="absolute bottom-0 left-0 right-0 p-4 pb-8">
        {/* Caption input for Quick Moment */}
        {mode === 'moment' && (
          <div className="mb-4">
            <input
              type="text"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder="Add a caption (optional)"
              className="w-full bg-white/15 text-white text-sm px-4 py-3 rounded-xl focus:outline-none focus:ring-1 focus:ring-purple-500 placeholder-gray-400"
              maxLength={200}
            />
          </div>
        )}

        {/* Action buttons */}
        <div className="flex items-center justify-center gap-12">
          <button
            onClick={onRetake}
            disabled={uploading}
            className="flex flex-col items-center gap-1 disabled:opacity-50"
          >
            <div className="w-14 h-14 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center transition-colors">
              <RotateCcw className="w-6 h-6 text-white" />
            </div>
            <span className="text-white text-xs">Retake</span>
          </button>

          <button
            onClick={handleSave}
            disabled={uploading}
            className="flex flex-col items-center gap-1 disabled:opacity-50"
          >
            <div className="w-20 h-20 rounded-full bg-gray-300 flex items-center justify-center">
              <div className="w-[72px] h-[72px] rounded-full bg-purple-600 hover:bg-purple-700 flex items-center justify-center transition-colors">
                <CloudUpload className="w-7 h-7 text-white" />
              </div>
            </div>
          </button>
        </div>
      </div>
    </div>
  );
}

export default RecordingReview;
